import { API_ENDPOINTS, BASE_URL } from '../../constants/apiEndpoints';
import { ActionEnum } from '../../enums/ActionEnum';
import fetcher from '../../helpers/fetcher';
import { BaseQuery } from '../../types/query';
import { unwrapDataResponse } from '../../utils/unwrapDataResponse';
import {
  UserActivateAccountRequest,
  UserChangePasswordRequest,
  UserDeleteRequest,
  UserPermissionGridSaveRequest,
  UserRegisterRequest,
  UserRoleCreateRequest,
  UserRoleDeleteRequest,
  UserLoginOTPRequest,
  UserForgetPasswordOTPRequest,
  UserResetPasswordRequest,
  UserLockUnlockRequest,
  UserUpdateRequest,
  CitizenRegisterRequest,
} from '../../types/user';

export const createUser = (data: UserRegisterRequest) => {
  return fetcher.post(API_ENDPOINTS.USER.CREATE_USER, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const deleteUser = (data: UserDeleteRequest) => {
  return fetcher.del(
    `${API_ENDPOINTS.USER.DELETE_USER}?id=${data.id}&versionData=${data.versionId}`,
    {},
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const lockUnlockUser = (data: UserLockUnlockRequest) => {
  return fetcher.patch(
    `${API_ENDPOINTS.USER.LOCK_UNLOCK_USER}?userId=${data.userId}&action=${data.action}`,
    {},
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const loginUser = (email: string, password: string, deviceToken: string) => {
  return fetcher.post(
    API_ENDPOINTS.USER.LOGIN,
    { email: email, password: password, deviceToken: deviceToken },
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE,
    ActionEnum.NO_REDIRECT
  );
};

export const forgetPassword = (email: string) => {
  return fetcher.post(
    API_ENDPOINTS.USER.FORGET_PASSWORD,
    { email },
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const changePassword = (data: UserChangePasswordRequest) => {
  return fetcher.post(
    API_ENDPOINTS.USER.CHANGE_PASSWORD,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE,
    ActionEnum.NO_REDIRECT
  );
};

export const activateUserAccount = (data: UserActivateAccountRequest) => {
  return fetcher.get(
    API_ENDPOINTS.USER.ACTIVATE_ACCOUNT,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getUserDetails = (filterQuery: any) => {
  return fetcher.post(
    API_ENDPOINTS.USER.USER_DETAILS,
    filterQuery,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getUserRoles = (data: BaseQuery) => {
  return fetcher.post(API_ENDPOINTS.USER.GET_ROLES, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const editUserData = (data: UserUpdateRequest) => {
  return fetcher.post(API_ENDPOINTS.USER.EDIT_USER, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const createUserRoles = (data: UserRoleCreateRequest) => {
  return fetcher.post(API_ENDPOINTS.USER.CREATE_ROLES, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const registerCitizen = (data: CitizenRegisterRequest) => {
  return fetcher.post(
    API_ENDPOINTS.USER.REGISTER_CANDIDATE,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const editUserRoles = (data: UserRoleCreateRequest) => {
  return fetcher.put(API_ENDPOINTS.USER.EDIT_ROLES, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const deleteUserRoles = (data: UserRoleDeleteRequest) => {
  return fetcher.del(
    `${API_ENDPOINTS.USER.DELETE_ROLES}?roleId=${data.id}`,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getSystemPermissions = (data: BaseQuery) => {
  return fetcher.post(
    API_ENDPOINTS.USER.GET_PERMISSIONS,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getUserRolePermissions = (data: BaseQuery) => {
  return fetcher.get(
    API_ENDPOINTS.USER.GET_ROLE_PERMISSIONS,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const updateUserPermissionGrid = (data: UserPermissionGridSaveRequest[]) => {
  return fetcher.put(
    API_ENDPOINTS.USER.UPDAGE_PERMISSION_GRID,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userSendLoginOTP = (data: UserLoginOTPRequest) => {
  return fetcher.post(API_ENDPOINTS.USER.SEND_LOGIN_OTP, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const userSendForgetPasswordOTP = (data: UserForgetPasswordOTPRequest) => {
  return fetcher.post(
    API_ENDPOINTS.USER.SEND_FORGET_PASSWORD_OTP,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userResendOTP = (otpId: number) => {
  return fetcher.post(
    API_ENDPOINTS.USER.RESEND_OTP,
    { otpId: otpId },
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userResendForgetPasswordOTP = (otpId: number) => {
  return fetcher.post(
    API_ENDPOINTS.USER.RESEND_FORGET_OTP,
    { otpId: otpId },
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userResetPassword = (data: UserResetPasswordRequest) => {
  return fetcher.post(API_ENDPOINTS.USER.RESET_PASSWORD, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const userGetUserProfileDetails = (data: any) => {
  return fetcher.get(
    API_ENDPOINTS.USER.GET_PROFILE_DETAILS,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userUpdateProfile = (data: any) => {
  return fetcher.post(
    API_ENDPOINTS.USER.UPDATE_PROFILE_DETAILS,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const updateProfilePicture = (data: any) => {
  return fetcher.post(
    API_ENDPOINTS.USER.UPDATE_PROFILE_PICTURE,
    { profileImage: data },
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const userDeleteProfilePicture = (_data: any) => {
  return fetcher.del(
    API_ENDPOINTS.USER.DELETE_PROFILE_PICTURE,
    {},
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getProfilePic = (data: any) => {
  return fetcher.get(
    API_ENDPOINTS.USER.GET_PROFILE_PICTURE,
    data,
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const resendActivationRequest = (data: { userId: string }) => {
  return fetcher.patch(
    `${API_ENDPOINTS.USER.RESEND_ACTIVATION_EMAIL}?userId=${data.userId}`,
    {},
    {},
    BASE_URL.ACCESS_MANAGEMENT_BASE
  );
};

export const getUserAuditLog = (data: BaseQuery) => {
  return fetcher.post(API_ENDPOINTS.USER.GET_AUDIT_LOG, data, {}, BASE_URL.ACCESS_MANAGEMENT_BASE);
};

export const getInspectorUsers = async (data: { roles?: string[] }) => {
  const response = await fetcher.get(
    API_ENDPOINTS.USER.INSPECTOR_USERS,
    data,
    {},
    BASE_URL.ANALYSIS_SERVICE_BASE
  );

  return {
    ...response,
    data: unwrapDataResponse<any>(response.data),
  };
};
